import React, { useState } from 'react';
import SEO from '../components/common/SEO';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import './Contact.css';
import { motion } from 'framer-motion';

interface FormData {
  name: string;
  email: string;
  company: string;
  service: string;
  message: string;
}

const Contact: React.FC = () => {
  const [validated, setValidated] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setShowSuccess(false);

    try {
      const response = await fetch('https://formspree.io/f/xrbybqnl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          service: '',
          message: ''
        });
        setValidated(false);
      } else {
        // Handle server-side errors from Formspree
        const result = await response.json();
        const errorMessage = result.errors?.map((err: any) => err.message).join(', ') || 'An unknown error occurred.';
        throw new Error(errorMessage);
      }
      
    } catch (err: any) {
      const errorMessage = err.message || 'There was an error submitting the form. Please try again later.';
      setError(errorMessage);
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Contact Us"
        description="Contact Skytion Enterprises for premium tissue product inquiries. Phone: +91 8920065965, Email: sales@skytion.in. Located in Ecotech Phase 3, Gautam Buddha Nagar, Noida."
        keywords="contact skytion, tissue supplier contact, bulk tissue orders, manufacturing inquiry, noida tissue company, wholesale tissue, export inquiry"
        schemaType="ContactPage"
        canonical="https://skytion.in/contact"
      />
      <section className="bg-light py-5 contact-hero-section">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} className="text-center">
              <motion.h1
                className="display-4 mb-4 section-title"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Contact Us
              </motion.h1>
              <motion.p
                className="lead mb-4 section-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Ready to source premium tissue products for your business? We're here to help!
              </motion.p>
              <div className="section-divider"></div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="contact-infographic-container"
              >
                <div className="infographic-header">
                  <h4 className="infographic-title">Manufacturing Excellence Network</h4>
                  <p className="infographic-subtitle">Premium Tissue Manufacturing & Global Distribution</p>
                </div>
                <img
                  src="/assets/skytion/company/Facility photo.png"
                  alt="Skytion Manufacturing Facility"
                  className="img-fluid contact-infographic"
                  style={{borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)'}}
                />
                <div className="network-stats">
                  <div className="network-stat" style={{ '--stat-color': '#14b8a6' } as React.CSSProperties}>
                    <span className="stat-number">24/7</span>
                    <span className="stat-label">Production</span>
                  </div>
                  <div className="network-stat" style={{ '--stat-color': '#10b981' } as React.CSSProperties}>
                    <span className="stat-number">3</span>
                    <span className="stat-label">Countries</span>
                  </div>
                  <div className="network-stat" style={{ '--stat-color': '#22c55e' } as React.CSSProperties}>
                    <span className="stat-number">99.9%</span>
                    <span className="stat-label">Quality</span>
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Manufacturing Showcase Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="mb-4">
                  <img 
                    src="/assets/skytion/brand/certification badge .png" 
                    alt="ISO Certified" 
                    style={{width: '60px', marginBottom: '1rem'}}
                  />
                </div>
                <h2 className="display-5 mb-4" style={{color: 'var(--primary)'}}>Why Choose Skytion?</h2>
                <p className="lead text-muted">
                  Premier tissue manufacturer with ISO certification, serving global markets since 2022
                </p>
              </motion.div>
            </Col>
          </Row>
          
          <Row className="g-4">
            <Col lg={4} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="manufacturing-feature-card card-tissue-bg h-100 p-4 rounded-4 shadow-sm">
                  <img 
                    src="/assets/skytion/icons/Softness Icon.png" 
                    alt="Quality" 
                    style={{width: '50px'}} 
                    className="mb-3"
                  />
                  <h5 className="mb-3">Premium Quality</h5>
                  <p className="text-muted">99.9% quality assurance with ISO standards and virgin pulp materials</p>
                </div>
              </motion.div>
            </Col>
            
            <Col lg={4} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="manufacturing-feature-card card-tissue-bg h-100 p-4 rounded-4 shadow-sm">
                  <img 
                    src="/assets/skytion/icons/Durability Icon.png" 
                    alt="Production" 
                    style={{width: '50px'}} 
                    className="mb-3"
                  />
                  <h5 className="mb-3">24/7 Production</h5>
                  <p className="text-muted">Continuous manufacturing to meet global demand with advanced technology</p>
                </div>
              </motion.div>
            </Col>
            
            <Col lg={4} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="manufacturing-feature-card card-tissue-bg h-100 p-4 rounded-4 shadow-sm">
                  <img 
                    src="/assets/skytion/icons/Absorbency Icon.png" 
                    alt="Global" 
                    style={{width: '50px'}} 
                    className="mb-3"
                  />
                  <h5 className="mb-3">Global Reach</h5>
                  <p className="text-muted">Serving UK, USA, UAE, France, and India with reliable international shipping</p>
                </div>
              </motion.div>
            </Col>
          </Row>

          {/* Product Showcase */}
          <Row className="mt-5">
            <Col lg={12}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <h4 className="mb-4" style={{color: 'var(--primary)'}}>Our Product Range</h4>
                <Row className="g-3">
                  <Col md={3} sm={6}>
                    <div className="product-preview-card card-tissue-bg p-3 rounded-3 shadow-sm">
                      <img 
                        src="/assets/skytion/products/toilet-paper.png" 
                        alt="Toilet Roll" 
                        className="img-fluid mb-2"
                        style={{maxHeight: '80px', objectFit: 'contain'}}
                      />
                      <small className="text-muted">Toilet Rolls</small>
                    </div>
                  </Col>
                  <Col md={3} sm={6}>
                    <div className="product-preview-card card-tissue-bg p-3 rounded-3 shadow-sm">
                      <img 
                        src="/assets/skytion/products/kitchen-towel-png.png" 
                        alt="Kitchen Towel" 
                        className="img-fluid mb-2"
                        style={{maxHeight: '80px', objectFit: 'contain'}}
                      />
                      <small className="text-muted">Kitchen Towels</small>
                    </div>
                  </Col>
                  <Col md={3} sm={6}>
                    <div className="product-preview-card card-tissue-bg p-3 rounded-3 shadow-sm">
                      <img 
                        src="/assets/skytion/products/2-ply-tissue-paper.png" 
                        alt="Dining Tissue" 
                        className="img-fluid mb-2"
                        style={{maxHeight: '80px', objectFit: 'contain'}}
                      />
                      <small className="text-muted">Dining Tissues</small>
                    </div>
                  </Col>
                  <Col md={3} sm={6}>
                    <div className="product-preview-card card-tissue-bg p-3 rounded-3 shadow-sm">
                      <img 
                        src="/assets/skytion/products/c-fold-tissue-paper.png" 
                        alt="C Fold Tissue" 
                        className="img-fluid mb-2"
                        style={{maxHeight: '80px', objectFit: 'contain'}}
                      />
                      <small className="text-muted">C Fold Tissues</small>
                    </div>
                  </Col>
                </Row>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="py-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="contact-form-container"
              >
                {showSuccess && (
                  <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
                    Thank you for your message! We'll get back to you soon.
                  </Alert>
                )}

                {error && (
                  <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                  </Alert>
                )}

                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your name.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid email address.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Company</Form.Label>
                    <Form.Control
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Enter your company name (optional)"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Service Interest</Form.Label>
                    <Form.Select 
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required>
                      <option value="">Select a service</option>
                      <option value="tissue-products">Tissue Products</option>
                      <option value="bulk-manufacturing">Bulk Manufacturing</option>
                      <option value="import-export">Import Export</option>
                      <option value="private-label">Private Label Manufacturing</option>
                      <option value="wholesale-distribution">Wholesale Distribution</option>
                      <option value="custom-development">Custom Product Development</option>
                      <option value="other">Other</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Please select a service.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter your message.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button 
                    variant="primary" 
                    type="submit" 
                    className="w-100"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </Button>
                </Form>
              </motion.div>
            </Col>

            <Col md={4} className="mt-4 mt-md-0">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="contact-info-container"
              >
                <h3 className="h4 mb-4">Get in Touch</h3>
                <div className="mb-4">
                  <h4 className="h6">Email</h4>
                  <p className="mb-0">sales@skytion.in</p>
                  <p className="mb-0">Website: www.skytion.in</p>
                </div>
                <div className="mb-4">
                  <h4 className="h6">Phone</h4>
                  <p className="mb-0">+91 8920065965</p>
                </div>
                <div className="mb-4">
                  <h4 className="h6">Address</h4>
                  <p className="mb-0">Skytion Enterprises</p>
                  <p className="mb-0">Ecotech Phase 3</p>
                  <p className="mb-0">Gautam Buddha Nagar, Noida</p>
                </div>
                <div className="mb-4">
                  <h4 className="h6">Markets Served</h4>
                  <p className="mb-0">India • UK • USA • UAE • France</p>
                  <p className="mb-0">& International Export</p>
                </div>
                <div className="text-center mb-4">
                  <img src="/assets/skytion/brand/skytion logo png.png" alt="Skytion.in Logo" className="img-fluid mt-3" style={{ maxHeight: '300px' }} />
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Contact;
